"use client";

import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue
);

function FormItem({ className, ...props }: React.ComponentProps<"div">) {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        data-slot="form-item"
        className={cn("grid gap-2", className)}
        {...props}
      />
    </FormItemContext.Provider>
  );
}

function FormLabel({
  className,
  ...props
}: React.ComponentProps<typeof LabelPrimitive.Root>) {
  const { error, formItemId } = useFormField();

  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      className={cn("data-[error=true]:text-destructive", className)}
      htmlFor={formItemId}
      {...props}
    />
  );
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
}

function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? "") : props.children;

  if (!body) {
    return null;
  }

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn("text-destructive text-sm", className)}
      {...props}
    >
      {body}
    </p>
  );
}

/**
 * FormFeedback - Enhanced feedback component with success states
 * Shows error messages in red, success messages in green
 * Supports custom validation messages for real-time feedback
 */
interface FormFeedbackProps extends React.ComponentProps<"div"> {
  successMessage?: string;
  showSuccessOnValid?: boolean;
}

function FormFeedback({
  className,
  successMessage,
  showSuccessOnValid = false,
  ...props
}: FormFeedbackProps) {
  const { error, formMessageId, isDirty, isTouched } = useFormField();

  // Show error state
  if (error) {
    return (
      <p
        data-slot="form-feedback"
        id={formMessageId}
        className={cn(
          "text-sm flex items-center gap-1.5 text-destructive animate-in fade-in-50 duration-200",
          className
        )}
        role="alert"
        {...props}
      >
        <svg
          className="h-3.5 w-3.5 flex-shrink-0"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M8 15A7 7 0 108 1a7 7 0 000 14zm.75-9.25a.75.75 0 00-1.5 0v4.5a.75.75 0 001.5 0v-4.5zM8 12a1 1 0 100-2 1 1 0 000 2z"
          />
        </svg>
        {String(error?.message ?? "")}
      </p>
    );
  }

  // Show success state when field is dirty/touched and valid
  if (showSuccessOnValid && (isDirty || isTouched) && successMessage) {
    return (
      <p
        data-slot="form-feedback"
        className={cn(
          "text-sm flex items-center gap-1.5 text-[var(--success)] animate-in fade-in-50 duration-200",
          className
        )}
        {...props}
      >
        <svg
          className="h-3.5 w-3.5 flex-shrink-0"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M8 15A7 7 0 108 1a7 7 0 000 14zm3.844-8.791a.75.75 0 00-1.188-.918l-3.7 4.79-1.649-1.833a.75.75 0 10-1.114 1.004l2.25 2.5a.75.75 0 001.151-.043l4.25-5.5z"
          />
        </svg>
        {successMessage}
      </p>
    );
  }

  return null;
}

/**
 * FormInputWrapper - Wraps input with validation indicator icons
 * Shows checkmark for valid, X for invalid fields
 */
interface FormInputWrapperProps extends React.ComponentProps<"div"> {
  showIcon?: boolean;
}

function FormInputWrapper({
  children,
  className,
  showIcon = true,
  ...props
}: FormInputWrapperProps) {
  const { error, isDirty, isTouched } = useFormField();
  const showValidation = showIcon && (isDirty || isTouched);
  const isValid = showValidation && !error;
  const isInvalid = showValidation && !!error;

  return (
    <div className={cn("relative", className)} {...props}>
      {children}
      {showValidation && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {isValid && (
            <svg
              className="h-4 w-4 text-[var(--success)] animate-in zoom-in-50 duration-200"
              viewBox="0 0 16 16"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8 15A7 7 0 108 1a7 7 0 000 14zm3.844-8.791a.75.75 0 00-1.188-.918l-3.7 4.79-1.649-1.833a.75.75 0 10-1.114 1.004l2.25 2.5a.75.75 0 001.151-.043l4.25-5.5z"
              />
            </svg>
          )}
          {isInvalid && (
            <svg
              className="h-4 w-4 text-destructive animate-in zoom-in-50 duration-200"
              viewBox="0 0 16 16"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8 15A7 7 0 108 1a7 7 0 000 14zm.75-9.25a.75.75 0 00-1.5 0v4.5a.75.75 0 001.5 0v-4.5zM8 12a1 1 0 100-2 1 1 0 000 2z"
              />
            </svg>
          )}
        </div>
      )}
    </div>
  );
}

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormFeedback,
  FormInputWrapper,
  FormField,
};
